import { useAtomValue, useSetAtom } from 'jotai';
import { useMutation } from '@apollo/client';
import { useCallback, useMemo } from 'react';
import { useRouter } from 'next/router';
import {
  IeltsWritingEndAttemptMutation,
  IeltsWritingSubmitPartAttemptMutation,
} from '@/api/common';
import { attemptIdAtom } from '@/store/ieltsSpeaking';
import { loadingSubmitIeltsAtom } from '@/store/ieltsListeningReading';
import { mockTestViolationCountAtom } from '@/store/mockTest';
import { cloneDeep } from 'lodash-es';
import { ieltsWritingCurrentPartAtom } from '@/store/ieltsWriting';
import { useFormContext } from 'react-hook-form';
import { EnglishSkillV1, IeltsDetailAnswerInput } from '@/__generated__/graphql';
import { AuthProviderViewerSessionQueryDocument } from '@/__generated__/graphql';
import useIeltsAttemptError from '@/components/ielts/common/hooks/useIeltsAttemptError';
import ROUTES from '@/lib/routes';
import { IeltsListeningReadingWritingFormValues } from '@/types';

function useDoIeltsWriting() {
  const router = useRouter();
  const attemptId = useAtomValue(attemptIdAtom) || '';
  const setLoadingSubmit = useSetAtom(loadingSubmitIeltsAtom);
  const activePart = useAtomValue(ieltsWritingCurrentPartAtom);
  const violationCount = useAtomValue(mockTestViolationCountAtom);

  const { handleAttemptError, handleUserErrors } = useIeltsAttemptError({
    skill: EnglishSkillV1.Writing,
    attemptId,
  });

  const { getValues, setValue } = useFormContext<IeltsListeningReadingWritingFormValues>() || {};

  const [writingSubmitPart, { loading: loadingSubmitPart }] = useMutation(
    IeltsWritingSubmitPartAttemptMutation,
    {
      context: {
        v2: true,
      },
    },
  );

  const [writingEndAttempt, { loading: loadingEndAttempt }] = useMutation(
    IeltsWritingEndAttemptMutation,
    {
      context: {
        v2: true,
      },
    },
  );

  const handleSubmitPart = useCallback(
    async ({ partId, answer }: { partId: string; answer: string }) => {
      await writingSubmitPart({
        variables: {
          input: {
            attemptId,
            partId,
            answer,
          },
        },
        // The essay autosaves on blur, so a failure here silently loses the
        // learner's work unless we say so.
        onError: (error) => {
          handleAttemptError(error, 'submit');
        },
        onCompleted: (res) => {
          handleUserErrors(res.ieltsWritingSubmitPartAttempt.userErrors, 'submit');
        },
      });
    },
    [attemptId, handleAttemptError, handleUserErrors, writingSubmitPart],
  );

  const handleSubmitPracticeIeltsPart = useCallback(
    async (input: { questionId: string; detailAnswer: IeltsDetailAnswerInput }) => {
      if (!activePart?.node.id || !attemptId) return;
      const selectedParts = getValues().parts;

      const partIndex = selectedParts.findIndex((p) => p.partId === activePart.node.id);
      const questionIndex = activePart.node.ieltsWritingPartQuestions.findIndex(
        (q) => q.node.id === input.questionId,
      );

      const detailAnswers = cloneDeep(
        getValues().parts[partIndex]?.questions[questionIndex]?.detailAnswers || [],
      );
      const detailIndex = detailAnswers.findIndex(
        (d) => d.detailId === input.detailAnswer.detailId,
      );

      if (detailIndex < 0) {
        return;
      }

      const existingDetailAnswers = cloneDeep(
        getValues().parts[partIndex]?.questions[questionIndex]?.detailAnswers || [],
      );

      detailAnswers[detailIndex] = input.detailAnswer;

      setValue(`parts.${partIndex}.questions.${questionIndex}.detailAnswers.${detailIndex}`, {
        ...existingDetailAnswers[detailIndex],
        ...input.detailAnswer,
      });

      await writingSubmitPart({
        variables: {
          input: {
            questionAnswer: {
              questionId: input.questionId.split('/')[1] || '',
              detailAnswers: detailAnswers.map((d) => ({
                detailId: d.detailId,
                text: d.text?.trim(),
                optionId: d.optionId,
                optionIds: d.optionIds,
              })),
            },
            attemptId,
            partId: activePart.node.ieltsWritingPartId,
          },
        },
        onError: (error) => {
          handleAttemptError(error, 'submit');
        },
        onCompleted: (res) => {
          handleUserErrors(res.ieltsWritingSubmitPartAttempt.userErrors, 'submit');
        },
      });
    },
    [
      activePart?.node.id,
      activePart?.node.ieltsWritingPartQuestions,
      attemptId,
      getValues,
      handleAttemptError,
      handleUserErrors,
      setValue,
      writingSubmitPart,
    ],
  );

  const handleEndAttempt = useCallback(async () => {
    if (loadingEndAttempt) return;
    setLoadingSubmit(true);

    await writingEndAttempt({
      variables: {
        input: {
          attemptId,
          violationCount,
        },
      },
      refetchQueries: [AuthProviderViewerSessionQueryDocument],
      awaitRefetchQueries: true,
      onCompleted: async (res) => {
        if (handleUserErrors(res.ieltsWritingEndAttempt.userErrors, 'end')) {
          setLoadingSubmit(false);
          return;
        }
        await router.push(ROUTES.CHECKPOINT_RESULT(EnglishSkillV1.Writing, attemptId));
      },
      onError: (error) => {
        setLoadingSubmit(false);
        handleAttemptError(error, 'end');
      },
    });
  }, [
    attemptId,
    handleAttemptError,
    handleUserErrors,
    loadingEndAttempt,
    router,
    setLoadingSubmit,
    writingEndAttempt,
    violationCount,
  ]);

  return useMemo(
    () => ({
      handleSubmitPart,
      loadingSubmitPart,
      handleEndAttempt,
      loadingEndAttempt,
      handleSubmitPracticeIeltsPart,
    }),
    [
      handleEndAttempt,
      handleSubmitPart,
      handleSubmitPracticeIeltsPart,
      loadingEndAttempt,
      loadingSubmitPart,
    ],
  );
}

export default useDoIeltsWriting;
